/**
 * Daily Check-In reward pop-up + streak "burned out" modal.
 *
 * The reward pop-up is shown on the post-game result screen (driven by game-modal.js from
 * the result endpoint's `checkIn` payload) and, as a fallback, once on home-page load when the
 * server flagged a reward the user never acknowledged (`#js-checkin-unshown-reward`). Whenever
 * it is displayed we POST to the mark-seen endpoint so it is not shown again. The burned-out
 * modal is shown once on home load when the server flagged a just-broken streak. Both reuse the
 * .s-streak-modal styling.
 */
import { launchConfetti } from './confetti';

function rewardModal() { return document.getElementById('js-checkin-reward-modal'); }

function markSeen(id) {
    const m = rewardModal();
    const url = m?.dataset.seenUrl;
    if (!url || !id) return;
    fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id }),
        keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
}

const portraitMql = window.matchMedia ? window.matchMedia('(orientation: portrait)') : null;
function isPortrait() { return portraitMql ? portraitMql.matches : true; }

let pendingReward = null;

// When a landscape game finishes with the phone still sideways, hold the celebration until the
// user rotates back to portrait — the pop-up is portrait-designed and the confetti shoots upward
// from the bottom of the viewport, so both only look right upright.
function onRotateToPortrait() {
    if (!isPortrait()) return;
    portraitMql?.removeEventListener('change', onRotateToPortrait);
    window.removeEventListener('orientationchange', onRotateToPortrait);
    const reward = pendingReward;
    pendingReward = null;
    if (reward) displayReward(reward);
}

/**
 * Show the reward pop-up (deferring until portrait if the device is currently landscape).
 * @param {{id:number, days:number, bonusPoints:number}} reward
 */
export function showCheckInReward(reward) {
    if (!reward || !rewardModal()) return;

    if (!isPortrait()) {
        pendingReward = reward;
        // addEventListener with the same handler ref is idempotent, so repeat calls are safe.
        portraitMql?.addEventListener('change', onRotateToPortrait);
        window.addEventListener('orientationchange', onRotateToPortrait);
        return;
    }

    displayReward(reward);
}

/** Populate + reveal the pop-up and mark it seen server-side. */
function displayReward(reward) {
    const m = rewardModal();
    if (!m) return;

    const days = reward.days ?? 0;
    const pts  = reward.bonusPoints ?? 0;

    m.querySelectorAll('.js-crm-days').forEach(el => { el.textContent = String(days); });
    m.querySelectorAll('.js-crm-points').forEach(el => { el.textContent = `+${pts}`; });

    // Always open on the claim panel.
    const claim  = m.querySelector('#js-crm-panel-claim');
    const reward_ = m.querySelector('#js-crm-panel-reward');
    if (claim)   claim.hidden   = false;
    if (reward_) reward_.hidden = true;

    m.hidden = false;
    launchConfetti();

    // Mark seen as soon as it is on screen — closing it must not make it reappear.
    markSeen(reward.id);
}

function hideRewardModal() {
    const m = rewardModal();
    if (m) m.hidden = true;
}

function readUnshownReward() {
    const el = document.getElementById('js-checkin-unshown-reward');
    if (!el) return null;
    try {
        return JSON.parse(el.textContent);
    } catch {
        return null;
    }
}

(function init() {
    const m = rewardModal();
    if (m) {
        // "Odbierz nagrodę" just reveals the reward panel — points were granted at check-in.
        m.querySelector('#js-crm-claim-btn')?.addEventListener('click', () => {
            const claim  = m.querySelector('#js-crm-panel-claim');
            const reward = m.querySelector('#js-crm-panel-reward');
            if (claim)  claim.hidden  = true;
            if (reward) reward.hidden = false;
        });
        m.querySelectorAll('.js-crm-close').forEach(btn => btn.addEventListener('click', hideRewardModal));
        m.addEventListener('click', (e) => { if (e.target === m) hideRewardModal(); });
    }

    // On home load, resurface an unacknowledged reward. It takes priority over the burned-out
    // modal so the two never stack.
    const unshown = readUnshownReward();
    if (unshown) {
        showCheckInReward(unshown);
        return;
    }

    // Streak burned-out modal — server-rendered only on the load where the streak just broke.
    const burned = document.getElementById('js-streak-burned-modal');
    if (burned) {
        burned.hidden = false;
        burned.addEventListener('click', (e) => { if (e.target === burned) burned.hidden = true; });
        burned.querySelectorAll('.js-streak-burned-close')
            .forEach(btn => btn.addEventListener('click', () => { burned.hidden = true; }));
    }
})();
